import React from 'react';
import { motion } from 'framer-motion';

const IndustriesSection = () => {
  const industries = [
    {
      icon: 'fa-building-columns',
      title: 'Finance & Banking',
      description: 'Risk analysis, fraud detection, and real-time profit monitoring. Banks pay top dollar for these insights.',
      color: 'pbi-yellow',
      bgIcon: 'fa-sack-dollar'
    },
    {
      icon: 'fa-user-doctor',
      title: 'Healthcare',
      description: 'Patient outcome tracking, hospital resource allocation, and insurance claims analysis.',
      color: 'green-500',
      bgIcon: 'fa-heart-pulse'
    },
    {
      icon: 'fa-shop',
      title: 'Retail & E-commerce',
      description: 'Inventory management, customer behavior tracking, and sales forecasting for giants like Amazon.',
      color: 'blue-500',
      bgIcon: 'fa-cart-shopping'
    },
    {
      icon: 'fa-boxes-stacked',
      title: 'Supply Chain',
      description: 'Tracking logistics, optimizing delivery routes, and reducing operational costs.',
      color: 'orange-500',
      bgIcon: 'fa-truck-fast'
    },
    {
      icon: 'fa-cloud',
      title: 'SaaS & Technology',
      description: 'User retention metrics, subscription growth analysis, and product usage insights.',
      color: 'purple-500',
      bgIcon: 'fa-microchip'
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  return (
    <section className="py-20 bg-pbi-dark relative border-b border-gray-800 animate-glow-border">
      <div className="container mx-auto px-6">
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="text-pbi-yellow font-bold uppercase tracking-wider text-sm mb-2 block">
            Industries Hiring International Students
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-white">
            Where Power BI Rules The World
          </h2>
          <p className="text-gray-400 mt-4 max-w-2xl mx-auto">
            It's not just "IT". These high-paying sectors are desperate for analysts who can turn raw data into decisions.
          </p>
        </motion.div>

        <motion.div 
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 perspective-1000"
        >
          {industries.map((industry, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              whileHover={{ 
                scale: 1.05,
                rotateX: 5,
                rotateY: 2,
                y: -10
              }}
              className="bg-gradient-to-br from-gray-900 to-black border border-gray-800 p-8 rounded-xl shadow-xl card-3d group relative overflow-hidden cursor-pointer"
            >
              <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                <i className={`fa-solid ${industry.bgIcon} text-8xl text-${industry.color}`}></i>
              </div>
              
              <motion.div 
                whileHover={{ scale: 1.1, rotate: 5 }}
                className={`w-14 h-14 bg-${industry.color}/10 rounded-lg flex items-center justify-center mb-6 transition-transform`}
              >
                <i className={`fa-solid ${industry.icon} text-2xl text-${industry.color}`}></i>
              </motion.div>
              
              <h3 className="text-xl font-bold text-white mb-2">{industry.title}</h3>
              <p className="text-gray-400 text-sm leading-relaxed">{industry.description}</p>
              
              <div className="mt-6 h-1 w-full bg-gray-800 rounded overflow-hidden">
                <motion.div 
                  initial={{ width: 0 }}
                  whileHover={{ width: '100%' }}
                  transition={{ duration: 0.7, ease: "easeOut" }}
                  className={`h-full bg-${industry.color}`}
                />
              </div>
            </motion.div>
          ))}

          <motion.div
            variants={itemVariants}
            whileHover={{ scale: 1.05 }}
            className="bg-gradient-to-br from-gray-800 to-gray-900 border-2 border-dashed border-gray-700 p-8 rounded-xl shadow-xl card-3d group relative overflow-hidden flex flex-col justify-center items-center text-center cursor-pointer"
          >
            <motion.div 
              animate={{ scale: [1, 1.1, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center mb-4"
            >
              <i className="fa-solid fa-question text-3xl text-white"></i>
            </motion.div>
            <h3 className="text-xl font-bold text-white mb-2">Your Future Role?</h3>
            <p className="text-gray-400 text-sm">
              Every industry needs data. Pick your path and start leading with insights.
            </p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default IndustriesSection;